import { Children, cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import { bool, func, oneOfType, node, string } from 'prop-types';
import { findIndex, map, noop, over } from 'lodash';
import clsx from 'clsx';
import { i18n } from '@lingui/core';
import { Accordion, AccordionSummary, List } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useDrag, useDrop } from 'react-dnd';

import messageDescriptor from '../../lingui/LinguiPropTypes';
import MenuActions, { actionsType } from './MenuActions';
import useStyles from './MenuStyles';
import { useIsExpanded } from './MenuUtilities';
import { useUser } from '../../../stores/SessionStore';

export const ITEM_TYPES = {
    GROUP_ITEM: 'group-item',
};

export const POSITIONS = {
    BEFORE: 'above',
    AFTER: 'below',
};

const emptyBounds = {
    x: 0,
    y: 0,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
};

function ItemDragContainer({ draggable, dragItemType, onEndDrag, onHovered, children }) {
    const classes = useStyles();
    const boundsRef = useRef();

    const [{ isDragging }, dragRef] = useDrag({
        type: dragItemType,
        item: { node: children, type: dragItemType },
        canDrag: () => draggable,

        end: (dragItem, monitor) => {
            onEndDrag(monitor.didDrop());
        },

        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    });

    const [, dropRef] = useDrop({
        accept: dragItemType,
        canDrop: () => false,
        hover: (dragItem, monitor) => {
            if (dragItem.node === children) return;

            const bounds = boundsRef?.current?.getBoundingClientRect() || emptyBounds;
            const middle = bounds.top + (bounds.bottom - bounds.top) / 2;
            const mouse = monitor.getClientOffset();

            onHovered(dragItem.node, mouse.y < middle ? POSITIONS.BEFORE : POSITIONS.AFTER);
        },
    });

    function attachRefs(el) {
        if (!draggable) return;
        boundsRef.current = el;
        dragRef(el);
        dropRef(el);
    }

    return (
        <div ref={attachRefs} className={clsx({ [classes.dragging]: isDragging })}>
            {children}
        </div>
    );
}

ItemDragContainer.propTypes = {
    draggable: bool,
    dragItemType: string,
    onHovered: func,
    onEndDrag: func,
    children: node.isRequired,
};

ItemDragContainer.defaultProps = {
    draggable: false,
    dragItemType: ITEM_TYPES.GROUP_ITEM,
    onHovered: noop,
    onEndDrag: noop,
};

export default function MenuGroup({ id, title, expanded, actions, children, enableReordering, onItemsReordered }) {
    const [items, setItems] = useState([]);
    const [user] = useUser();
    const [isExpanded, setIsExpanded] = useIsExpanded(user, id, expanded || !title);
    const dragItemType = `${id}-${ITEM_TYPES.GROUP_ITEM}`;
    const [, dropRef] = useDrop({
        accept: dragItemType,
    });

    const cloneChild = useCallback(
        (item) =>
            cloneElement(item, {
                onActiveChange: over([
                    item.props.onActiveChange,
                    (itemLink, active) => {
                        // Expand the group if any item is active.
                        if (active) setIsExpanded(true);
                    },
                ]),
            }),
        [setIsExpanded]
    );

    useEffect(() => {
        setItems(Children.toArray(children).map(cloneChild));
    }, [children, cloneChild]);

    /**
     *
     * @param {React.ReactHTMLElement} hoveredItem
     * @param {React.ReactHTMLElement} draggedItem
     * @param {'below' | 'above'} position
     */
    function handleItemHovered(hoveredItem, draggedItem, position) {
        const hoveredItemId = hoveredItem?.props?.id;
        const draggedItemId = draggedItem?.props?.id;

        const offset = position === POSITIONS.BEFORE ? 1 : 0;
        const len = items.length;
        const dragIdx = findIndexOfChildByPropId(items, draggedItemId);
        const hoverIdx = findIndexOfChildByPropId(items, hoveredItemId);
        const replaceIdx = Math.max(0, Math.min(len - 1, hoverIdx - offset));

        const newItems = [...items];
        const tmp = newItems[replaceIdx];
        newItems[replaceIdx] = newItems[dragIdx];
        newItems[dragIdx] = tmp;

        setItems(newItems);
    }

    function handleEndDrag(didDrop) {
        if (didDrop) {
            const newItems = map(items, 'props.dragItem');
            onItemsReordered(newItems);
        } else {
            setItems(Children.toArray(children).map(cloneChild));
        }
    }

    return (
        <Accordion
            id={`side-nav-group-${id}`}
            expanded={isExpanded}
            onChange={(event, state) => setIsExpanded(state)}
            elevation={0}
        >
            {title && (
                <AccordionSummary expandIcon={Children.count(children) > 0 && <ExpandMoreIcon />}>
                    {i18n._(title)}
                    <MenuActions actions={actions} />
                </AccordionSummary>
            )}
            <List ref={dropRef}>
                {items.map((item) => (
                    <ItemDragContainer
                        key={item.props.id || item.key}
                        draggable={enableReordering}
                        dragItemType={dragItemType}
                        onEndDrag={handleEndDrag}
                        onHovered={(draggedItem, position) => handleItemHovered(item, draggedItem, position)}
                    >
                        {item}
                    </ItemDragContainer>
                ))}
            </List>
        </Accordion>
    );
}

function findIndexOfChildByPropId(items, id) {
    return findIndex(items, (item) => item?.props?.id === id);
}

MenuGroup.propTypes = {
    id: string.isRequired,
    title: oneOfType([string, messageDescriptor]),
    expanded: bool,
    actions: actionsType,
    children: node,
    enableReordering: bool,
    onItemsReordered: func,
};

MenuGroup.defaultProps = {
    title: undefined,
    expanded: false,
    actions: undefined,
    children: undefined,
    enableReordering: false,
    onItemsReordered: noop,
};
